import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram, BadgeCheck, ArrowUpRight } from "lucide-react";
import { BRAND } from "@/data/site";
import { PRODUCT_MENU, FEATURE_MENU, FREE_TOOLS } from "@/data/catalog";
import { INDUSTRY_LIST } from "@/data/industries";
import NewsletterForm from "@/components/NewsletterForm";

// Official Meta infinity mark (cleanest at any size)
const MetaMark = ({ className = "h-6 w-auto" }) => (
  <svg viewBox="0 0 287 192" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Meta">
    <defs>
      <linearGradient id="meta-grad-a" x1="62" y1="117" x2="260" y2="127" gradientUnits="userSpaceOnUse">
        <stop stopColor="#0064E1" />
        <stop offset="0.4" stopColor="#0064E1" />
        <stop offset="0.83" stopColor="#0073EE" />
        <stop offset="1" stopColor="#0082FB" />
      </linearGradient>
      <linearGradient id="meta-grad-b" x1="41" y1="146" x2="41" y2="74" gradientUnits="userSpaceOnUse">
        <stop stopColor="#0082FB" />
        <stop offset="1" stopColor="#0064E0" />
      </linearGradient>
    </defs>
    <path d="M31.06 126c0 11 2.41 19.41 5.56 24.51 4.13 6.69 10.29 9.52 16.57 9.52 8.1 0 15.51-2.01 29.79-21.76 11.44-15.83 24.92-38.05 33.99-51.98l15.36-23.6c10.67-16.39 23.02-34.61 37.18-46.96C181.08 5.6 193.41 0 205.81 0c20.83 0 40.67 12.07 55.85 34.71C278.27 59.5 286.39 90.72 286.39 122.95c0 19.16-3.78 33.23-10.2 44.34-6.2 10.74-18.29 21.46-38.63 21.46v-30.61c17.42 0 21.77-16 21.77-34.31 0-26.1-6.09-55.07-19.5-75.78-9.51-14.69-21.84-23.67-35.41-23.67-14.69 0-26.51 11.08-39.79 30.85-7.06 10.51-14.31 23.32-22.45 37.78l-9.06 16.05c-18.2 32.27-22.81 39.62-31.91 51.75C95.34 181.74 81.81 192 63.4 192c-21.03 0-34.33-9.11-42.57-22.84C14.07 158.05 11 142.07 11 121.92Z" fill="#0081FB"/>
    <path d="M24.49 36.87C38.57 15.16 58.89 0 82.19 0c13.49 0 26.91 3.99 40.92 15.43 15.31 12.5 31.64 33.08 52.04 67.01l7.32 12.18c17.65 29.37 27.69 44.49 33.57 51.62 7.55 9.17 12.84 11.9 19.71 11.9 17.42 0 21.77-16 21.77-34.31l27.01-.85c0 19.16-3.78 33.23-10.2 44.34-6.2 10.74-18.29 21.46-38.63 21.46-12.64 0-23.84-2.75-36.22-14.42-9.52-8.95-20.65-24.85-29.21-39.18l-25.46-42.55C151.04 81.55 139.27 64.21 132.45 56.31 125.12 47.81 115.69 37.55 100.65 37.55c-12.17 0-22.5 8.54-31.15 21.61L24.49 36.87Z" fill="url(#meta-grad-a)"/>
    <path d="M82.19 30.65c-12.17 0-22.5 8.54-31.15 21.61C39.62 69.27 32.66 94.04 32.66 117.71c0 10.66 2.36 18.84 5.43 23.83L11.69 158.97C5.06 148.51 0 132.97 0 119.07 0 91.69 7.51 63.18 21.81 41.13 34.34 21.79 52.4 8.21 73.13 8.21Z" fill="url(#meta-grad-b)"/>
  </svg>
);

export default function Footer() {
  return (
    <footer className="relative overflow-hidden bg-[#0F172A] text-slate-300 mt-12" data-testid="site-footer">
      {/* decorative top accent */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#25D366]/60 to-transparent" />
      <div className="absolute -top-32 -left-20 w-96 h-96 bg-[#25D366]/10 blur-3xl rounded-full pointer-events-none" />
      <div className="absolute -bottom-32 -right-20 w-96 h-96 bg-[#0082FB]/10 blur-3xl rounded-full pointer-events-none" />

      {/* Meta Tech Provider banner */}
      <div className="relative border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 rounded-3xl border border-white/10 bg-gradient-to-r from-white/[0.04] via-white/[0.06] to-white/[0.04] p-6 backdrop-blur" data-testid="footer-meta-banner">
            <div className="flex items-start md:items-center gap-5">
              <div className="shrink-0 w-16 h-16 rounded-2xl bg-white grid place-items-center shadow-lg shadow-[#0082FB]/30">
                <MetaMark className="h-8 w-auto" />
              </div>
              <div>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-emerald-300 font-semibold">
                  <BadgeCheck className="w-3.5 h-3.5" /> Verified Partnership
                </div>
                <div className="mt-1 font-display text-xl sm:text-2xl font-bold text-white leading-tight flex items-center gap-1.5 flex-wrap">
                  Official <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white">
                    <MetaMark className="h-4 w-auto" />
                    <span className="font-display text-lg sm:text-xl font-bold tracking-tight text-[#0082FB]">Meta</span>
                  </span> Tech Provider
                </div>
                <p className="mt-1 text-sm text-slate-400 max-w-xl">
                  GrabNChat is a recognised Tech Provider for the WhatsApp Business Platform — built and operated directly on Meta&apos;s Cloud API.
                </p>
              </div>
            </div>
            <a
              href="https://developers.facebook.com/docs/whatsapp/cloud-api"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 self-start md:self-auto px-5 h-11 rounded-full bg-white text-slate-900 font-semibold text-sm hover:bg-slate-100 transition-colors"
              data-testid="footer-meta-cta"
            >
              Learn about WhatsApp Cloud API <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Main footer grid */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-2 mb-4">
              <img src={BRAND.logoWebp} alt="GrabNChat logo" width="196" height="40" loading="lazy" className="h-10 w-auto bg-white rounded-md p-1" />
            </div>
            <p className="text-sm text-slate-400 leading-relaxed mb-6 max-w-sm">
              {BRAND.tagline} GrabNChat is an official WhatsApp Business API platform helping businesses
              automate marketing, sales and support at scale.
            </p>
            <NewsletterForm />
            <div className="flex items-center gap-4 mt-6">
              <a href="#" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-social-twitter"><Twitter className="w-5 h-5" /></a>
              <a href="#" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-social-linkedin"><Linkedin className="w-5 h-5" /></a>
              <a href="#" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-social-facebook"><Facebook className="w-5 h-5" /></a>
              <a href="#" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-social-instagram"><Instagram className="w-5 h-5" /></a>
            </div>
          </div>

          <FooterCol title="Products" items={PRODUCT_MENU.map((p) => ({ to: `/products/${p.slug}`, label: p.title }))} testPrefix="footer-product" />
          <FooterCol title="Features" items={[...FEATURE_MENU.map((p) => ({ to: `/products/${p.slug}`, label: p.title })), { to: "/features", label: "All features" }]} testPrefix="footer-feature" />
          <div className="md:col-span-2">
            <FooterList title="Industries" items={INDUSTRY_LIST.map((i) => ({ to: `/industries/${i.slug}`, label: i.title.replace("WhatsApp for ", "") }))} testPrefix="footer-industry" />
            <div className="mt-8"><FooterList title="Free tools" items={FREE_TOOLS} testPrefix="footer-tool" /></div>
          </div>
          <div className="md:col-span-2">
            <FooterList title="Platform" items={[
              { to: "/pricing", label: "Pricing" },
              { to: "/integrations", label: "Integrations" },
              { to: "/features", label: "Features" },
              { to: "/docs", label: "Docs & API" },
              { to: "/#faq", label: "FAQs" },
            ]} testPrefix="footer-platform" />
            <div className="mt-8"><FooterList title="Company" items={[
              { to: "/contact", label: "Contact us" },
              { to: "/contact", label: "Book a demo" },
              { href: `${BRAND.appUrl}/login`, label: "Login" },
            ]} testPrefix="footer-company" /></div>
          </div>
        </div>

        {/* Contact strip */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          <a href={`mailto:${BRAND.email}`} className="group flex items-start gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:border-[#25D366]/40 transition-colors" data-testid="footer-contact-email">
            <div className="w-10 h-10 rounded-lg bg-[#25D366]/15 text-[#25D366] grid place-items-center"><Mail className="w-4 h-4" /></div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Email</div>
              <div className="text-sm font-semibold text-white truncate group-hover:text-[#25D366]">{BRAND.email}</div>
            </div>
          </a>
          <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="group flex items-start gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:border-[#25D366]/40 transition-colors" data-testid="footer-contact-phone">
            <div className="w-10 h-10 rounded-lg bg-[#25D366]/15 text-[#25D366] grid place-items-center"><Phone className="w-4 h-4" /></div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Call us</div>
              <div className="text-sm font-semibold text-white group-hover:text-[#25D366]">{BRAND.phone}</div>
            </div>
          </a>
          <div className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/5" data-testid="footer-contact-address">
            <div className="w-10 h-10 rounded-lg bg-[#25D366]/15 text-[#25D366] grid place-items-center"><MapPin className="w-4 h-4" /></div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Office</div>
              <div className="text-sm font-medium text-slate-200 leading-snug">{BRAND.address}</div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-slate-900 text-xs font-semibold" data-testid="footer-meta-provider">
              <MetaMark className="h-4 w-auto" />
              <span className="font-display font-bold text-[#0082FB] text-sm">Meta</span>
              <span className="text-slate-300">·</span>
              Tech Provider
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-xs font-medium text-slate-300">
              <BadgeCheck className="w-3.5 h-3.5 text-[#25D366]" /> Official WhatsApp Business API
            </span>
          </div>
          <div className="text-xs text-slate-500">
            © {new Date().getFullYear()} {BRAND.copyright}. All Rights Reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterList({ title, items, testPrefix }) {
  return (
    <>
      <p className="text-white text-sm font-semibold mb-4">{title}</p>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it.label}>
            {it.href ? (
              <a href={it.href} className="text-sm text-slate-400 hover:text-white transition-colors">{it.label}</a>
            ) : (
              <Link to={it.to} className="text-sm text-slate-400 hover:text-white transition-colors" data-testid={`${testPrefix}-${it.label.toLowerCase().replace(/\W+/g, "-")}`}>{it.label}</Link>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

function FooterCol({ title, items, testPrefix }) {
  return (
    <div className="md:col-span-2">
      <FooterList title={title} items={items} testPrefix={testPrefix} />
    </div>
  );
}
